import { corelCommunication } from './communication';
import { sendToTerminal } from '../terminalLogger';

export type PhotoshopOptions = {
  photoshopPath?: string;
  format?: 'TIFF' | 'PNG';
  dpi?: number;
  colorMode?: string;
  embedICC?: boolean;
  compression?: 'LZW' | 'NONE';
  tempDir?: string;
  timeoutMs?: number;
  debounceMs?: number;
  allowFlatten?: boolean;
  replaceInDoc?: boolean;
  targetLayer?: string;
  positionMode?: string;
  workFilePath?: string;
};

export type MeasureAutoDimensionsOptions = {
  unit: 'mm' | 'cm';
  unitIndex?: number; // Fallback para compatibilidade (0=mm, 1=cm, 2=in)
  distanceMm: number; // Número real, não string
  lineThicknessMm: number; // Número real, não string
  textSizePt: number; // Número real, em pontos (pt)
  scale: string;
  position: number;
  includeUnitSuffix: boolean;
  simpleMode: boolean;
  quantity?: number; // Quantidade como número (opcional)
  presetId: string;
  savePreset: boolean;
};

export type BitmapConvertOptions = {
  // Tool Bitmap.Convert.Simple: opções simplificadas
  colorMode: 'RGB' | 'CMYK';
  transparent: boolean;
  dpi: number;
};

export type CorelWorksPlanResult = {
  status?: 'ok' | 'error';
  Status?: 'ok' | 'error'; // Propriedade com maiúscula da API
  dryRun?: boolean;
  DryRun?: boolean; // Propriedade com maiúscula da API
  plan?: unknown;
  Plan?: unknown; // Propriedade com maiúscula da API
  warnings?: string[];
  Warnings?: string[]; // Propriedade com maiúscula da API
  affectedObjects?: number;
  AffectedObjects?: number; // Propriedade com maiúscula da API
  outputs?: unknown[];
  Outputs?: unknown[]; // Propriedade com maiúscula da API
  metrics?: { elapsedMs?: number; planElapsedMs?: number; execElapsedMs?: number };
  Metrics?: { elapsedMs?: number; planElapsedMs?: number; execElapsedMs?: number }; // Propriedade com maiúscula da API
};

export const CorelWorks = {
  detectPhotoshop: () => {
    return new Promise<Array<{ path: string; version?: string }> | null>((resolve) => {
      const data = {} as const;
      const id = corelCommunication.sendActionGuarded('system:detect-photoshop', data, (resp: any) => {
        resolve((resp?.result?.candidates as any) || null);
      });
      if (!id) resolve(null);
    });
  },

  openFileDialog: (initialDir?: string) => {
    const data = { filter: 'Executáveis|Photoshop*.exe;*.exe|Todos os arquivos|*.*', initialDir } as const;
    return new Promise<string | null>((resolve) => {
      const id = corelCommunication.sendActionGuarded('system:open-file-dialog', data, (resp: any) => {
        resolve((resp?.result?.path as string) || null);
      });
      if (!id) resolve(null);
    });
  },

  validateExecutable: (path: string) => {
    const data = { path } as const;
    return new Promise<{ ok: boolean; version?: string; reason?: string }>((resolve) => {
      const id = corelCommunication.sendActionGuarded('system:validate-executable', data, (resp: any) => {
        const ok = resp?.result?.ok ?? resp?.status === 'ok';
        resolve({ ok, version: resp?.result?.version, reason: resp?.result?.reason });
      });
      if (!id) resolve({ ok: false, reason: 'NoSession' });
    });
  },

  planExternalEditPhotoshop: (options: PhotoshopOptions = {}, scope: 'Selection' | 'Document' = 'Selection') => {
    // ⚠️ IMPORTANTE: sessionToken NÃO deve estar em data - buildMessage() coloca na raiz automaticamente
    const data = { toolId: 'External.Edit.Photoshop', scope, dryRun: true, options } as const;
    return new Promise<CorelWorksPlanResult>((resolve) => {
      const id = corelCommunication.sendActionGuarded('corelworks:tool:plan', data, (resp: any) => {
        resolve(resp?.result || { status: resp?.status === 'ok' ? 'ok' : 'error', dryRun: true });
      });
      if (!id) resolve({ status: 'error', dryRun: true });
    });
  },

  executeExternalEditPhotoshop: (options: PhotoshopOptions = {}, scope: 'Selection' | 'Document' = 'Selection') => {
    // ⚠️ IMPORTANTE: sessionToken NÃO deve estar em data - buildMessage() coloca na raiz automaticamente
    const data = { toolId: 'External.Edit.Photoshop', scope, dryRun: false, options } as const;
    // Timeout estendido: aguarda abertura do Photoshop + processamento
    const baseTimeout = typeof options?.timeoutMs === 'number' ? options.timeoutMs : 900_000; // default 15min
    const customTimeoutMs = Math.max(900_000, baseTimeout + 120_000); // +2min de margem
    return new Promise<any>((resolve) => {
      const id = corelCommunication.sendActionGuarded('corelworks:tool', data, (resp: any) => {
        // Para Run PS, retornamos a resposta completa para preservar status/mensagens do host
        resolve(resp);
      }, customTimeoutMs);
      if (!id) resolve({ status: 'error', message: 'Sessão indisponível', dryRun: false });
    });
  },

  planMeasureAutoDimensions: (options: MeasureAutoDimensionsOptions, scope: 'SelectionOnly' | 'Document' = 'SelectionOnly') => {
    const data = { 
      apiVersion: "1.0",
      toolId: 'Measure.AutoDimensions', 
      scope, 
      dryRun: true, 
      correlationId: `plan-${Date.now()}`,
      options 
    } as const;
    return new Promise<CorelWorksPlanResult>((resolve) => {
      const id = corelCommunication.sendActionGuarded('corelworks:tool:plan', data, (resp: any) => {
        resolve(resp?.result || { status: resp?.status === 'ok' ? 'ok' : 'error', dryRun: true });
      });
      if (!id) resolve({ status: 'error', dryRun: true });
    });
  },

  executeMeasureAutoDimensions: (options: MeasureAutoDimensionsOptions, scope: 'SelectionOnly' | 'Document' = 'SelectionOnly') => {
    const data = { 
      apiVersion: "1.0",
      toolId: 'Measure.AutoDimensions', 
      scope, 
      dryRun: false, 
      correlationId: `exec-${Date.now()}`,
      options 
    } as const;
    return new Promise<CorelWorksPlanResult>((resolve) => {
      const id = corelCommunication.sendActionGuarded('corelworks:tool', data, (resp: any) => {
        resolve(resp?.result || { status: resp?.status === 'ok' ? 'ok' : 'error', dryRun: false });
      });
      if (!id) resolve({ status: 'error', dryRun: false });
    });
  },

  executeBitmapConvert: (options: BitmapConvertOptions, _scope?: 'Selection' | 'All') => {
    // Scope é sempre a seleção ativa na Tool Simple; param mantido apenas por compatibilidade
    const data = {
      toolId: 'Bitmap.Convert.Simple',
      dryRun: false,
      options
    } as const;
    return new Promise<CorelWorksPlanResult>((resolve) => {
      const id = corelCommunication.sendActionGuarded('corelworks:tool', data, (resp: any) => {
        // A Tool Simple não emite progress_update; por segurança, ignore se vier
        if (resp?.status === 'progress_update') return;
        resolve(resp?.result || { status: resp?.status === 'ok' ? 'ok' : 'error', dryRun: false });
      });
      if (!id) resolve({ status: 'error', dryRun: false });
    });
  }
};


